import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { ContextValueType, PropsType } from "../types/index";
import sessionAxios from "../utils/sessionAxios";

export const AuthContext = createContext<ContextValueType>({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<PropsType> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [activeStatus, setActiveStatus] = useState<number>(1);
  const [activeSetting, setActiveSetting] = useState<number>(1);
  // const [page, setPage] = useState<string>("/dashboard");
  const [payoutUserId, setPayoutUserId] = useState<string>("");
  const [customerId, setCustomerId] = useState<string>("");
  const [transactionId, setTransactionId] = useState<string>("");
  const [cryptoModal, setCryptoModal] = useState<boolean>(false);
  const [withdrawModal, setWithdrawModal] = useState<boolean>(false);
  const [uploadGiftcard, setUploadGiftcard] = useState<boolean>(false);
  const [isNotified, setIsNotified] = useState<boolean>(false);
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [urlStatus, setUrlStatus] = useState<string>("");
  const [failedPayouts, setFailedpayouts] = useState<any[]>([]);
  const [openRange, setOpenRange] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>("");
  const [openButton, setOpenButton] = useState<boolean>(false);
  const [query, setQuery] = useState<string>("");
  const [userActivities, setUserActivities] = useState<boolean>(false);
  const [clientId, setClientId] = useState<string>("");
  const [customer, setCustomer] = useState<number>(1);
  /**
   * Which sidebar accordion group is expanded. A single value (rather than one
   * boolean per group) is what makes opening one collapse the other.
   */
  const [openNavGroup, setOpenNavGroup] = useState<
    "customer-txn" | "users" | "kyc" | null
  >(null);

  const [openBroadcast, setOpenBroadcast] = useState<boolean>(false);

  /**
   * The session is an httpOnly cookie, so JS can't read it — "am I logged in?" is answered
   * by asking the API. GET api/users/me is the designated bootstrap: 200 = yes, 401 = no.
   */
  const refreshSession = useCallback(async () => {
    try {
      await sessionAxios.get("api/users/me");
      setIsAuthenticated(true);
      return true;
    } catch {
      setIsAuthenticated(false);
      return false;
    } finally {
      setAuthReady(true);
    }
  }, []);

  useEffect(() => {
    // Admins still carry the pre-migration `admin-token` cookie, which was JS-readable and
    // holds a live JWT. Bin it — the httpOnly cookie is the session now.
    document.cookie = "admin-token=; Max-Age=0; path=/";
    refreshSession();
  }, [refreshSession]);

  useEffect(() => {
    if (location.pathname === "/dashboard") {
      setActiveStatus(1);
    } else if (location.pathname === "/wallet") {
      setActiveStatus(2);
    } else if (location.pathname === "/users") {
      setActiveStatus(5);
      setOpenNavGroup("users");
    } else if (location.pathname === "/users/new-signups") {
      setActiveStatus(51);
      setOpenNavGroup("users");
    } else if (location.pathname === "/settings") {
      setActiveStatus(7);
    } else if (location.pathname === "/payouts") {
      setActiveStatus(81);
      setOpenNavGroup("customer-txn");
    } else if (location.pathname === "/giftcards") {
      setActiveStatus(82);
      setOpenNavGroup("customer-txn");
    } else if (location.pathname === "/bill-payments") {
      setActiveStatus(83);
      setOpenNavGroup("customer-txn");
    } else if (location.pathname === "/deex-desk") {
      setActiveStatus(84);
    } else if (location.pathname === "/merchants") {
      setActiveStatus(85);
    } else if (
      location.pathname === "/transactions" ||
      location.pathname === "/orders"
    ) {
      setActiveStatus(8);
      setOpenNavGroup("customer-txn");
    } else if (location.pathname === "/reports") {
      setActiveStatus(10);
    } else if (location.pathname === "/rewards") {
      setActiveStatus(11);
    } else if (location.pathname === "/invite-codes") {
      setActiveStatus(12);
    } else if (location.pathname === "/kyc-logs") {
      setActiveStatus(13);
      setOpenNavGroup("kyc");
    } else if (location.pathname === "/kyc-logs/compliance") {
      setActiveStatus(131);
      setOpenNavGroup("kyc");
    } else if (location.pathname === "/kyc-logs/rules") {
      setActiveStatus(132);
      setOpenNavGroup("kyc");
    }
  }, [location.pathname]);

  // useEffect(() => {
  //   if (!sellPage) {
  //     setCryptoPages(1);
  //   }
  // }, [sellPage]);

  /**
   * Called after a successful login response. The backend has already set the cookie —
   * there is no token for us to store, so this just re-reads the session.
   */
  const authenticate = useCallback(async () => {
    const ok = await refreshSession();
    if (!ok) {
      throw new Error("Signed in, but no session was established.");
    }
    return "";
  }, [refreshSession]);

  /**
   * We can't delete an httpOnly cookie ourselves — the server has to, and it revokes the
   * session at the same time. Local state is cleared regardless of the response so a failed
   * request can never strand someone in a logged-in-looking UI.
   */
  const logout = useCallback(async () => {
    try {
      await sessionAxios.post("api/auth/logout");
    } catch {
      // Already expired or revoked — nothing left to clear server-side.
    }
    setIsAuthenticated(false);
    // Drop every cached query, or the next admin in this tab briefly sees the last one's data.
    queryClient.clear();
    navigate("/login");
  }, [navigate, queryClient]);
  return (
    <AuthContext.Provider
      value={{
        customer,
        setCustomer,
        clientId,
        setClientId,
        setTransactionId,
        transactionId,
        userActivities,
        setUserActivities,
        query,
        setQuery,
        openButton,
        setOpenButton,
        filter,
        setFilter,
        openRange,
        setOpenRange,
        failedPayouts,
        setFailedpayouts,
        urlStatus,
        setUrlStatus,
        isOpened,
        setIsOpened,
        isNotified,
        setIsNotified,
        uploadGiftcard,
        setUploadGiftcard,
        withdrawModal,
        setWithdrawModal,
        customerId,
        setCustomerId,
        payoutUserId,
        setPayoutUserId,

        cryptoModal,
        setCryptoModal,

        isAuthenticated,
        authReady,
        refreshSession,
        authenticate,
        logout,
        userOpen,
        setUserOpen,
        activeStatus,
        setActiveStatus,
        orderOpen,
        setOrderOpen,
        activeSetting,
        setActiveSetting,

        openBroadcast,
        setOpenBroadcast,
        openNavGroup,
        setOpenNavGroup,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
